// packages/web/src/lib/outbox.ts — S5c: THE DURABLE WRITE QUEUE.
//
// S5a shipped the offline banner and refused to fake the count:
// `pendingSendCount()` returned zero, always, because there was no queue for
// anything to wait in. This module is that queue, and it is what makes wf03's
// central promise true — "Nothing is lost. You can change any of this until it
// sends."
//
// WHAT AN ENTRY IS. An INTENT, never a rendered result: the tenant, the words,
// the blobs, the page the Knot was pressed on, and the bookkeeping (attempts,
// last failure, a client-generated id). Nothing derived, because nothing has
// been derived — no server has seen it. That is exactly why the tech may still
// edit it (wf03: "there is no return window until there is something to
// return").
//
// WHY IndexedDB. Not localStorage: synchronous, ~5 MB, string-only, and this
// queue holds audio and photos. Not memory: the entire point is surviving a
// reload, a dead battery, and a phone in a pocket for six hours.
//
// WHY BYTES, NOT BLOBS (schema v2, S5e). v1 stored the File objects
// themselves — IndexedDB structured-clones a File, so "audio stored on this
// phone" looked literally true. On WebKit it was not: WebKit DETACHES a
// Blob/File persisted in IndexedDB once its owning page is gone, and a
// detached blob serializes into fetch's FormData as an EMPTY part with no
// error anywhere. Reproduced live on iOS 18.7 (2026-08-14): a voice capture
// queued offline, the page reloaded, reconnect drained — and the POST arrived
// at the server with `content-length: 0`, verified on the wire, twice.
// Chromium round-trips a stored File fine, which is exactly why the S5c/S5d
// Chromium walks never caught it. So an entry's files are now
// `{ name, type, bytes: ArrayBuffer }` — the bytes are read AT ENQUEUE TIME,
// while the blob is certainly alive, and the drain rebuilds a fresh File per
// send. ArrayBuffers structured-clone into IndexedDB durably on WebKit.
//
// WHERE THERE IS NO IndexedDB (happy-dom, which this package's tests run in,
// and a browser in a hostile privacy mode) this module falls back to an
// in-memory store and SAYS SO on the console — once. A silent fallback would
// be a queue that claims durability it does not have, which is the one lie
// this file exists to prevent. The consequence is stated plainly in the
// report: the unit suite exercises this module's LOGIC against the memory
// store, and the IndexedDB path is proven in a real browser instead.
//
// ORDER IS THE WHOLE CONTRACT. Entries send strictly in enqueue order and the
// drain STOPS at the first one that does not send — it never skips ahead. A
// material line that references a visit captured moments earlier must not land
// before it.
//
// WHAT SENDING AN ENTRY MEANS: exactly one call, `POST /api/actor/capture` —
// the same hand-over the sheet makes online. It does NOT approve the proposal
// that comes back. The first version of this file did, on the reasoning that a
// tech who pressed the button offline never saw a propose card to approve; a
// real browser walk against a real server killed that idea in one request. A
// TECH CANNOT APPROVE: the server answers 403 "role 'member' does not meet the
// operation's minimum role 'admin'". Auto-approving was therefore both a claim
// on the tech's behalf and a call that could not succeed for the very person
// this queue exists for. So the queue delivers the capture — words and bytes
// retained server-side, a proposal staged in the ordinary review queue — and
// what becomes of that proposal follows exactly the same path as a capture
// made with five bars. The receipt says "handed over", never "filed", for that
// reason.
//
// IDEMPOTENCY, HONESTLY. `POST /api/actor/capture` (packages/server/src/
// plugins/actorCapture.ts) accepts text, files and context and NOTHING else —
// there is no idempotency key on the wire today, and inventing one here would
// be inventing a server contract. With one call per entry the exposure is
// narrow but real: a request that reached the server and whose RESPONSE was
// lost retries and may capture twice. Reported as a Core gap, not papered over.
import { ApiError, captureKnot, type CaptureContext } from './api';
import { bumpRecordsVersion } from './recordEditor';

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

/** `queued` — waiting, will send itself.
 *  `sending` — in flight RIGHT NOW; frozen, because editing a request that is
 *    already on the wire is how a tech's correction gets silently discarded.
 *  `blocked` — the server REFUSED it (4xx, or 5xx past the attempt cap).
 *    Retrying will never fix it, so it stops retrying and surfaces. */
export type OutboxEntryState = 'queued' | 'sending' | 'blocked';

export interface OutboxFailure {
  /** `unreachable` — the request never landed (offline, DNS, timeout). Normal,
   *  silent, retried forever.
   *  `server` — a 5xx; retried with backoff up to the cap.
   *  `refused` — a 4xx; the server understood and said no. Never retried.
   *  `device` — THIS PHONE lost the payload: a pre-v2 entry whose stored File
   *  came back detached at migration (see the module header). No retry can
   *  bring the bytes back, so it blocks and says so in honest words. */
  kind: 'unreachable' | 'server' | 'refused' | 'device';
  status?: number;
  message: string;
  at: number;
}

/** Schema v2 — what an entry actually keeps for each attached file: the
 *  bytes themselves, read while the blob was alive. Never a File/Blob (see
 *  the module header for the WebKit detachment this exists to survive). */
export interface StoredCaptureFile {
  name: string;
  type: string;
  bytes: ArrayBuffer;
}

export interface OutboxEntry {
  id: string;
  /** Monotonic per device. Two captures inside one millisecond still have an
   *  order, and it is this one. */
  seq: number;
  tenantId: string;
  text: string;
  files: StoredCaptureFile[];
  context: CaptureContext;
  createdAt: number;
  attempts: number;
  state: OutboxEntryState;
  lastError?: OutboxFailure;
  /** Earliest wall-clock time this entry may be tried again (5xx backoff). */
  nextAttemptAt?: number;
}

/** Rebuild real Files from an entry's stored bytes — what the drain sends and
 *  what the composer edits. Fresh Files every call: a File handed out earlier
 *  may belong to a page that has since died, which is the whole lesson. */
export function entryFiles(entry: Pick<OutboxEntry, 'files'>): File[] {
  return entry.files.map((f) => new File([f.bytes], f.name, { type: f.type }));
}

/** Read a live File's bytes NOW. Called at enqueue/edit time, never at send
 *  time — at send time the blob may already be a WebKit ghost. */
async function keepBytes(files: File[]): Promise<StoredCaptureFile[]> {
  return Promise.all(
    files.map(async (f) => ({ name: f.name, type: f.type, bytes: await f.arrayBuffer() }))
  );
}

export interface EnqueueInput {
  tenantId: string;
  text: string;
  files: File[];
  context: CaptureContext;
}

/** What the banner needs, and nothing more. `waiting` counts what will send
 *  itself; `blocked` counts what will not. They are separate numbers on
 *  purpose: a banner that says "2 waiting · they will send themselves" while
 *  one of the two is refused is a small lie, and this file's whole job is to
 *  not tell it. */
export interface OutboxCounts {
  waiting: number;
  blocked: number;
}

// A 5xx is retryable, but not forever: past this many attempts it stops being
// a blip and starts being a fact the tech needs to know about.
const MAX_SERVER_ATTEMPTS = 5;
const BACKOFF_BASE_MS = 5_000;
const BACKOFF_CAP_MS = 5 * 60_000;

// ---------------------------------------------------------------------------
// Storage — IndexedDB, with a loud in-memory fallback
// ---------------------------------------------------------------------------

const DB_NAME = 'rexx-outbox';
// v2 — files became { name, type, bytes } (S5e). The object store's shape is
// unchanged (same keyPath, same index); the version bump marks the entry
// payload change, and migrateLegacyEntries below rewrites v1 rows after open
// (the rewrite awaits Blob reads, which an upgrade transaction cannot — an
// IDB transaction auto-commits the moment no request is pending).
const DB_VERSION = 2;
const STORE = 'entries';

interface OutboxStore {
  readonly durable: boolean;
  all(): Promise<OutboxEntry[]>;
  put(entry: OutboxEntry): Promise<void>;
  del(id: string): Promise<void>;
  clear(): Promise<void>;
}

function memoryStore(): OutboxStore {
  const rows = new Map<string, OutboxEntry>();
  return {
    durable: false,
    async all() {
      return [...rows.values()].sort((a, b) => a.seq - b.seq);
    },
    async put(entry) {
      rows.set(entry.id, entry);
    },
    async del(id) {
      rows.delete(id);
    },
    async clear() {
      rows.clear();
    },
  };
}

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexeddb request failed'));
  });
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('seq', 'seq', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexeddb open failed'));
    req.onblocked = () => reject(new Error('indexeddb open blocked'));
  });
}

function idbStore(db: IDBDatabase): OutboxStore {
  function tx(mode: IDBTransactionMode): IDBObjectStore {
    return db.transaction(STORE, mode).objectStore(STORE);
  }
  return {
    durable: true,
    async all() {
      const rows = await promisify(tx('readonly').getAll() as IDBRequest<OutboxEntry[]>);
      return rows.sort((a, b) => a.seq - b.seq);
    },
    async put(entry) {
      await promisify(tx('readwrite').put(entry));
    },
    async del(id) {
      await promisify(tx('readwrite').delete(id));
    },
    async clear() {
      await promisify(tx('readwrite').clear());
    },
  };
}

// ---------------------------------------------------------------------------
// v1 -> v2 migration — attempt to read every stored File's bytes; a detached
// one BLOCKS the entry with honest words. NEVER silently drops an entry.
// ---------------------------------------------------------------------------

/** What a v1 row carried per file: a real File/Blob (alive or, on WebKit
 *  after its owning page died, detached). Read with care — a detached blob
 *  rejects (or lies with zero bytes) instead of erroring loudly. */
interface LegacyStoredFile {
  name?: string;
  type?: string;
  size?: number;
  arrayBuffer: () => Promise<ArrayBuffer>;
}

function isStoredBytes(f: unknown): f is StoredCaptureFile {
  return !!f && typeof f === 'object' && 'bytes' in f;
}

/** One entry, migrated. Returns null when the entry is already v2 (nothing
 *  to write). A readable legacy File becomes bytes; an unreadable (detached)
 *  one cannot — the recording could not be kept on this device — so the entry
 *  blocks and surfaces through the existing "N needs you · not sent" door
 *  (FieldStatus renders lastError.message), rather than draining an empty
 *  body at the server or vanishing. */
async function migrateLegacyEntry(entry: OutboxEntry): Promise<OutboxEntry | null> {
  const raw = entry.files as unknown as Array<StoredCaptureFile | LegacyStoredFile>;
  if (raw.every(isStoredBytes)) return null;
  const kept: StoredCaptureFile[] = [];
  const lost: string[] = [];
  for (const f of raw) {
    if (isStoredBytes(f)) {
      kept.push(f);
      continue;
    }
    const name = f.name || 'file';
    try {
      const bytes = await f.arrayBuffer();
      // WebKit's other face of detachment: the read "succeeds" and hands back
      // nothing where the file claimed real content. Zero bytes for a file
      // that said it had some is the recording being gone, not a migration.
      if (bytes.byteLength === 0 && (f.size ?? 0) > 0) {
        lost.push(name);
        continue;
      }
      kept.push({ name, type: f.type ?? '', bytes });
    } catch {
      lost.push(name);
    }
  }
  if (lost.length === 0) return { ...entry, files: kept };
  return {
    ...entry,
    files: kept,
    state: 'blocked',
    lastError: {
      kind: 'device',
      message:
        lost.length === 1
          ? `“${lost[0]}” could not be kept on this device — the phone let go of the recording before it could send.`
          : `${lost.length} recordings could not be kept on this device — the phone let go of them before they could send.`,
      at: Date.now(),
    },
  };
}

/** The whole sweep, run once per open against the durable store. */
async function migrateLegacyEntries(s: OutboxStore): Promise<void> {
  for (const entry of await s.all()) {
    const migrated = await migrateLegacyEntry(entry);
    if (migrated) await s.put(migrated);
  }
}

let storePromise: Promise<OutboxStore> | null = null;

function store(): Promise<OutboxStore> {
  if (!storePromise) {
    storePromise = (async () => {
      if (typeof indexedDB === 'undefined') {
        warnNotDurable('this engine has no IndexedDB');
        return memoryStore();
      }
      try {
        const s = idbStore(await openDb());
        await migrateLegacyEntries(s);
        return s;
      } catch (err) {
        warnNotDurable(String(err));
        return memoryStore();
      }
    })();
  }
  return storePromise;
}

let warned = false;
function warnNotDurable(why: string): void {
  if (warned) return;
  warned = true;
  // Loud on purpose. A queue that quietly stops surviving reloads is worse
  // than no queue, because the promise on screen stays the same.
  console.warn(
    `[outbox] NOT DURABLE — captures will not survive a reload (${why}). ` +
      'Falling back to memory.'
  );
}

// ---------------------------------------------------------------------------
// The synchronous count, and who hears about it
// ---------------------------------------------------------------------------

let counts: OutboxCounts = { waiting: 0, blocked: 0 };
const listeners = new Set<(c: OutboxCounts) => void>();

/** The last known counts, synchronously. IndexedDB is async and the banner
 *  renders now, so the truth is cached here and refreshed on every mutation —
 *  which is what makes `pendingSendCount()` a real number instead of a
 *  constant. */
export function outboxCounts(): OutboxCounts {
  return counts;
}

/** Subscribe to changes. Returns the unsubscribe. This is the reactivity S5c
 *  §4 asks for: the banner counts down as the queue drains, with no reload. */
export function subscribeOutbox(fn: (c: OutboxCounts) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function publish(next: OutboxCounts): void {
  if (next.waiting === counts.waiting && next.blocked === counts.blocked) return;
  counts = next;
  for (const fn of [...listeners]) fn(counts);
}

async function recount(): Promise<OutboxCounts> {
  const entries = await (await store()).all();
  publish({
    waiting: entries.filter((e) => e.state !== 'blocked').length,
    blocked: entries.filter((e) => e.state === 'blocked').length,
  });
  return counts;
}

// ---------------------------------------------------------------------------
// The queue itself
// ---------------------------------------------------------------------------

let seqCounter = 0;

function nextSeq(entries: OutboxEntry[]): number {
  const highest = entries.reduce((max, e) => Math.max(max, e.seq), 0);
  seqCounter = Math.max(seqCounter, highest) + 1;
  return seqCounter;
}

function newId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `ob-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Put an intended capture on this device. Returns the entry, which is what
 *  the receipt renders. The files' BYTES are read here, immediately — the
 *  blobs are certainly alive at enqueue time, and only bytes survive a
 *  WebKit reload (module header). */
export async function enqueueCapture(input: EnqueueInput): Promise<OutboxEntry> {
  const files = await keepBytes(input.files);
  const s = await store();
  const entry: OutboxEntry = {
    id: newId(),
    seq: nextSeq(await s.all()),
    tenantId: input.tenantId,
    text: input.text,
    files,
    context: input.context,
    createdAt: Date.now(),
    attempts: 0,
    state: 'queued',
  };
  await s.put(entry);
  await recount();
  return entry;
}

/** Every entry, in send order. */
export async function listOutbox(): Promise<OutboxEntry[]> {
  return (await store()).all();
}

export async function getOutboxEntry(id: string): Promise<OutboxEntry | null> {
  const entries = await (await store()).all();
  return entries.find((e) => e.id === id) ?? null;
}

/** EDITABLE WHILE PENDING, FROZEN ONCE SENDING. Returns the updated entry, or
 *  null when it is mid-flight or already gone — the caller must show what
 *  actually happened rather than assume the edit took. An edit also clears a
 *  block: the tech has changed the thing that was refused, so it is owed
 *  another try. */
export async function editQueuedCapture(
  id: string,
  patch: { text?: string; files?: File[] }
): Promise<OutboxEntry | null> {
  // Bytes first (the files come straight from the composer, alive), store
  // second — same order as enqueueCapture, for the same reason.
  const patchedFiles = patch.files ? await keepBytes(patch.files) : undefined;
  const s = await store();
  const entry = (await s.all()).find((e) => e.id === id);
  if (!entry || entry.state === 'sending') return null;
  const next: OutboxEntry = {
    ...entry,
    text: patch.text ?? entry.text,
    files: patchedFiles ?? entry.files,
    state: 'queued',
    attempts: 0,
    nextAttemptAt: undefined,
    lastError: undefined,
  };
  await s.put(next);
  await recount();
  return next;
}

/** The tech's escape hatch. A refused entry holds its place in the order (the
 *  queue must not skip ahead), so discarding it is what releases anything
 *  behind it. Deliberate, never automatic. */
export async function discardOutboxEntry(id: string): Promise<void> {
  const s = await store();
  const entry = (await s.all()).find((e) => e.id === id);
  if (!entry || entry.state === 'sending') return;
  await s.del(id);
  await recount();
}

/** A refused entry the tech has changed nothing about, tried once more —
 *  offered because a 4xx is sometimes a state that has since moved (a work
 *  order reopened, a membership granted). */
export async function retryBlockedEntry(id: string): Promise<void> {
  const s = await store();
  const entry = (await s.all()).find((e) => e.id === id);
  if (!entry || entry.state !== 'blocked') return;
  await s.put({ ...entry, state: 'queued', attempts: 0, nextAttemptAt: undefined });
  await recount();
  await drainOutbox();
}

// ---------------------------------------------------------------------------
// Sending
// ---------------------------------------------------------------------------

function classify(err: unknown): OutboxFailure {
  const at = Date.now();
  if (err instanceof ApiError) {
    if (err.status >= 400 && err.status < 500) {
      return { kind: 'refused', status: err.status, message: err.message, at };
    }
    return { kind: 'server', status: err.status, message: err.message, at };
  }
  // fetch itself rejected: offline, DNS, connection reset, timeout. The
  // request NEVER LANDED. This is the normal case in a walk-in cooler and it
  // must stay silent.
  return {
    kind: 'unreachable',
    message: err instanceof Error ? err.message : 'the network did not answer',
    at,
  };
}

function backoffMs(attempts: number): number {
  return Math.min(BACKOFF_BASE_MS * 2 ** Math.max(0, attempts - 1), BACKOFF_CAP_MS);
}

type SendOutcome = 'sent' | 'retry' | 'blocked';

async function sendEntry(entry: OutboxEntry): Promise<SendOutcome> {
  const s = await store();
  await s.put({ ...entry, state: 'sending' });
  await recount();

  try {
    // ONE call. The hand-over, and nothing on the tech's behalf beyond it —
    // see this file's header for the 403 that settled that. The Files are
    // rebuilt FRESH from stored bytes per send: a rebuilt File belongs to
    // THIS page, so WebKit cannot have detached it.
    await captureKnot(entry.tenantId, entry.text, entryFiles(entry), entry.context);
    await s.del(entry.id);
    await recount();
    return 'sent';
  } catch (err) {
    const failure = classify(err);
    const attempts = entry.attempts + 1;
    const blocked =
      failure.kind === 'refused' ||
      (failure.kind === 'server' && attempts >= MAX_SERVER_ATTEMPTS);
    await s.put({
      ...entry,
      attempts,
      state: blocked ? 'blocked' : 'queued',
      lastError: failure,
      nextAttemptAt:
        failure.kind === 'server' && !blocked ? Date.now() + backoffMs(attempts) : undefined,
    });
    await recount();
    return blocked ? 'blocked' : 'retry';
  }
}

let draining: Promise<void> | null = null;
let backoffTimer: ReturnType<typeof setTimeout> | null = null;

/** Send everything that can be sent, IN ORDER, stopping at the first entry
 *  that does not go. Never skips ahead: a dependent write landing before the
 *  thing it depends on is the bug this rule exists to prevent. Concurrent
 *  calls collapse onto the one in flight. */
export async function drainOutbox(): Promise<void> {
  if (draining) return draining;
  draining = (async () => {
    try {
      let sentAny = false;
      for (;;) {
        const entries = await (await store()).all();
        const head = entries[0];
        if (!head) break;
        if (head.state === 'blocked') break; // surfaced, not skipped
        if (head.nextAttemptAt && head.nextAttemptAt > Date.now()) {
          scheduleWake(head.nextAttemptAt - Date.now());
          break;
        }
        if (typeof navigator !== 'undefined' && navigator.onLine === false) break;
        const outcome = await sendEntry(head);
        if (outcome === 'sent') {
          sentAny = true;
          continue;
        }
        if (outcome === 'retry') {
          const again = await getOutboxEntry(head.id);
          if (again?.nextAttemptAt) scheduleWake(again.nextAttemptAt - Date.now());
        }
        break;
      }
      // ONE refresh for the whole drain, not one per entry: every mounted
      // screen re-reads on this signal, and a per-entry bump would turn a
      // ten-item queue into ten screen-wide read storms on a phone that has
      // just this second got its signal back.
      if (sentAny) bumpRecordsVersion();
    } finally {
      draining = null;
    }
  })();
  return draining;
}

function scheduleWake(ms: number): void {
  if (typeof setTimeout !== 'function') return;
  if (backoffTimer) clearTimeout(backoffTimer);
  backoffTimer = setTimeout(() => {
    backoffTimer = null;
    void drainOutbox();
  }, Math.max(250, ms));
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

let started = false;

/** Idempotent. Called on import rather than from App.tsx — the queue belongs
 *  to the device, not to a component tree, and it must come back to life on a
 *  cold start whatever screen the tech happens to land on. */
export function startOutbox(): void {
  if (started) return;
  started = true;
  void (async () => {
    const s = await store();
    // A tab that died mid-flight leaves an entry marked `sending` and nothing
    // to finish it. It is queued again. If its request had in fact reached the
    // server, this is the duplicate the missing idempotency key would prevent —
    // named here rather than hidden.
    for (const e of await s.all()) {
      if (e.state === 'sending') await s.put({ ...e, state: 'queued' });
    }
    await recount();
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => void drainOutbox());
      // A phone comes back from a pocket without ever firing `online` — the
      // radio never dropped, the tab was just frozen. Coming back to the
      // foreground is the other moment worth trying.
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
          if (!document.hidden) void drainOutbox();
        });
      }
    }
    if (counts.waiting > 0) await drainOutbox();
  })();
}

if (typeof window !== 'undefined') startOutbox();

/** Tests only — the v1→v2 sweep pieces, exercised directly because happy-dom
 *  has no IndexedDB to upgrade (the store shape they run against is the same
 *  OutboxStore contract the IDB path migrates through). */
export const __migrateLegacyEntryForTests = migrateLegacyEntry;
export const __migrateLegacyEntriesForTests = migrateLegacyEntries;
export type { OutboxStore };

/** Tests only — drops every entry and forgets the store, so each case starts
 *  from an empty device. */
export async function __resetOutboxForTests(): Promise<void> {
  const s = await store();
  await s.clear();
  storePromise = null;
  seqCounter = 0;
  counts = { waiting: 0, blocked: 0 };
  if (backoffTimer) {
    clearTimeout(backoffTimer);
    backoffTimer = null;
  }
  draining = null;
}
